import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  ArrowUpTrayIcon,
  BanknotesIcon,
  BoltIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  CurrencyEuroIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  MoonIcon,
  SparklesIcon,
  Squares2X2Icon,
  BuildingOffice2Icon,
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const LOGIN_PATH = '/a';

const appModules = [
  { id: 'dashboard', name: 'Panel', description: 'Indicadores, evolución anual y resumen trimestral IVA/IRPF', icon: Squares2X2Icon },
  { id: 'invoices', name: 'Facturas', description: 'Crear, editar, previsualizar, enviar y hacer seguimiento', icon: DocumentTextIcon },
  { id: 'clients', name: 'Clientes', description: 'Directorio de clientes vinculado a tus facturas y cobros', icon: UserGroupIcon },
  {
    id: 'import',
    name: 'Importar',
    description: 'Varios PDF a la vez: la IA extrae los datos y tú confirmas',
    icon: ArrowUpTrayIcon,
    badge: 'IA'
  },
  {
    id: 'work-expenses',
    name: 'Gastos',
    description: 'Gastos deducibles del trabajo, organizados por trimestre',
    icon: BanknotesIcon
  },
  {
    id: 'gestoria',
    name: 'Gestoría',
    description: 'Export trimestral, resumen fiscal y paquetes para tu asesoría',
    icon: BriefcaseIcon
  },
  {
    id: 'auto-billing',
    name: 'Auto-facturación',
    description: 'Cobros periódicos programados por cliente',
    icon: CalendarDaysIcon
  },
  { id: 'email', name: 'Email', description: 'Envío de facturas desde tu cuenta de Gmail', icon: EnvelopeIcon },
  { id: 'settings', name: 'Ajustes', description: 'Correo, IA, copias, multiempresa y WooCommerce opcional', icon: Cog6ToothIcon }
];

const statCards = [
  { label: 'Facturas totales', value: '48', icon: DocumentTextIcon, color: 'text-blue-600', bg: 'bg-blue-100' },
  { label: 'Ingresos totales', value: '42.350 €', icon: CurrencyEuroIcon, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  { label: 'Facturas pagadas', value: '31', icon: CheckCircleIcon, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  { label: 'Pendientes de cobro', value: '12', icon: ClockIcon, color: 'text-amber-600', bg: 'bg-amber-100' },
  { label: 'Vencidas', value: '5', icon: ExclamationTriangleIcon, color: 'text-rose-600', bg: 'bg-rose-100' },
  { label: 'Gastos del trimestre', value: '8', icon: BanknotesIcon, color: 'text-violet-600', bg: 'bg-violet-100' }
];

const mockInvoices = [
  {
    num: '2025-0048',
    client: 'Estudio Creativo SL',
    concepts: 'Diseño web · Mantenimiento',
    amount: '1.240,00 €',
    status: 'Pagada',
    statusClass: 'bg-emerald-100 text-emerald-700'
  },
  {
    num: '2025-0047',
    client: 'Tech Solutions SA',
    concepts: 'Consultoría · 3 líneas',
    amount: '890,50 €',
    status: 'Enviada',
    statusClass: 'bg-sky-100 text-sky-700'
  },
  {
    num: '2025-0046',
    client: 'Consultoría Norte',
    concepts: 'Gastos no suplidos · IVA 21%',
    amount: '2.100,00 €',
    status: 'Pendiente',
    statusClass: 'bg-amber-100 text-amber-700'
  }
];

const features = [
  {
    title: 'Facturación con IVA e IRPF',
    description:
      'Conceptos, gastos suplidos y no suplidos, bases, retenciones y totales adaptados a la facturación habitual en España.',
    icon: DocumentTextIcon,
    span: 'lg:col-span-2'
  },
  {
    title: 'Importación asistida por IA',
    description:
      'Sube uno o varios PDF. La IA propone número, cliente, conceptos e importes; tú revisas y confirmas antes de guardar.',
    icon: SparklesIcon,
    span: ''
  },
  {
    title: 'Asistente fiscal con IA',
    description:
      'Consulta ingresos, gastos, trimestres y facturas en lenguaje natural. Te guía sin sustituir a tu gestoría.',
    icon: ChatBubbleLeftRightIcon,
    span: ''
  },
  {
    title: 'Hub de gestoría',
    description:
      'Resumen trimestral IVA/IRPF, exportación CSV/ZIP de gastos y material listo para entregar a tu asesoría.',
    icon: BriefcaseIcon,
    span: ''
  },
  {
    title: 'Panel fiscal trimestral',
    description: 'Ingresos, estados de cobro, evolución anual y resumen de IVA e IRPF calculados con tus facturas.',
    icon: ChartBarIcon,
    span: ''
  },
  {
    title: 'Gastos del trabajo',
    description: 'Registra gastos deducibles por trimestre, separados de lo que facturas a tus clientes.',
    icon: BanknotesIcon,
    span: ''
  },
  {
    title: 'Multiempresa y permisos',
    description: 'Cambia de sociedad al instante. Roles de usuario y administrador para equipos o asesorías.',
    icon: BuildingOffice2Icon,
    span: ''
  },
  {
    title: 'WooCommerce (opcional)',
    description:
      'Conecta tu tienda online, sincroniza pedidos y genera facturas desde pedidos cuando lo necesites.',
    icon: ShoppingBagIcon,
    span: ''
  },
  {
    title: 'Envío por Gmail',
    description: 'Conecta tu cuenta en Ajustes y envía facturas desde Email o desde el listado.',
    icon: EnvelopeIcon,
    span: ''
  },
  {
    title: 'Cobros recurrentes',
    description: 'Programa facturas periódicas: mantenimientos, dominios, cuotas de servicio y más.',
    icon: CalendarDaysIcon,
    span: ''
  },
  {
    title: 'Interfaz moderna',
    description: 'Modo oscuro, búsqueda rápida con ⌘K, menú lateral adaptable y un diseño limpio y consistente.',
    icon: MoonIcon,
    span: 'lg:col-span-2'
  }
];

const settingsItems = [
  { name: 'Gmail', description: 'Conecta tu cuenta para enviar facturas por correo', icon: EnvelopeIcon },
  { name: 'Inteligencia artificial', description: 'Configura el modelo y comprueba la conexión', icon: SparklesIcon },
  { name: 'Google Drive', description: 'Sincronización en beta desde Ajustes', icon: CloudArrowUpIcon },
  { name: 'Copia de seguridad', description: 'Exporta y restaura tus datos en JSON', icon: ArrowUpTrayIcon },
  { name: 'Usuarios y empresas', description: 'Gestiona sociedades, accesos y permisos multi-usuario', icon: BuildingOffice2Icon },
  { name: 'Auto-facturación', description: 'Define reglas de cobro periódico', icon: CalendarDaysIcon },
  { name: 'WooCommerce', description: 'Integración opcional con tu tienda online', icon: ShoppingBagIcon }
];

function PrimaryCta({ className = '', large = false }: { className?: string; large?: boolean }) {
  return (
    <Link
      to={LOGIN_PATH}
      className={`inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 font-semibold text-white shadow-lg shadow-violet-600/25 transition hover:bg-violet-500 hover:shadow-xl hover:shadow-violet-500/30 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 ${
        large ? 'px-8 py-4 text-base' : 'px-6 py-3.5 text-sm'
      } ${className}`}
    >
      Acceder a FactuApp
      <ArrowRightIcon className={large ? 'h-5 w-5' : 'h-4 w-4'} aria-hidden />
    </Link>
  );
}

function BrowserFrame({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border shadow-2xl ring-1 ${
        dark
          ? 'border-zinc-800/90 bg-zinc-950 shadow-black/40 ring-white/5'
          : 'border-slate-200/90 bg-slate-100 shadow-slate-900/10 ring-slate-900/5'
      }`}
    >
      <div
        className={`flex items-center gap-2 border-b px-4 py-3 ${
          dark ? 'border-zinc-800 bg-zinc-900/80' : 'border-slate-200 bg-slate-50'
        }`}
      >
        <span className="h-3 w-3 rounded-full bg-rose-400" />
        <span className="h-3 w-3 rounded-full bg-amber-400" />
        <span className="h-3 w-3 rounded-full bg-emerald-400" />
        <span
          className={`ml-3 flex-1 rounded-md px-3 py-1 text-center text-xs ${
            dark ? 'bg-zinc-800 text-zinc-500' : 'bg-white text-slate-400'
          }`}
        >
          factuapp.entikmedia.com
        </span>
      </div>
      {children}
    </div>
  );
}

function DashboardMockPreview() {
  const chartHeights = [42, 58, 48, 72, 65, 80, 55, 68, 75, 62, 88, 70];
  const sidebarModules = appModules.slice(0, 7);

  return (
    <BrowserFrame dark>
      <div className="flex min-h-[420px] bg-[#0a0a0b] text-zinc-100">
        <aside className="hidden w-36 shrink-0 border-r border-zinc-800 bg-[#0f0f11]/95 p-2 sm:block">
          <p className="px-2 py-2 text-xs font-semibold tracking-tight">FactuApp</p>
          <nav className="mt-1 space-y-0.5">
            {sidebarModules.map((mod, i) => {
              const Icon = mod.icon;
              return (
                <div
                  key={mod.id}
                  className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-[10px] font-medium ${
                    i === 0 ? 'bg-violet-600 text-white' : 'text-zinc-500'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="truncate">{mod.name}</span>
                  {mod.badge && (
                    <span className="ml-auto rounded bg-violet-500/30 px-1 text-[8px] text-violet-200">{mod.badge}</span>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        <div className="min-w-0 flex-1 p-3">
          <div className="mb-2 flex items-center gap-2 rounded-lg border border-zinc-800 bg-[#0f0f11]/70 px-2 py-1.5">
            <span className="flex flex-1 items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900/50 px-2 py-1 text-[10px] text-zinc-500">
              <span className="text-zinc-600">⌘K</span> Buscar…
            </span>
            <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] text-emerald-400">IA on</span>
            <MoonIcon className="h-3.5 w-3.5 text-zinc-500" aria-hidden />
          </div>

          <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-2.5 py-2">
            <div>
              <p className="text-xs font-bold text-zinc-100">Mi Empresa SL</p>
              <p className="text-[10px] text-zinc-500">facturacion@miempresa.com</p>
            </div>
            <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[9px] font-medium text-amber-300">
              Vista de ejemplo
            </span>
          </div>

          <div className="mb-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {statCards.slice(0, 6).map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-2">
                  <div className="flex items-start justify-between gap-1">
                    <Icon className="h-3 w-3 text-violet-400" aria-hidden />
                    <span className="text-[8px] uppercase tracking-wide text-zinc-600">ejemplo</span>
                  </div>
                  <p className="mt-1 text-[9px] text-zinc-500">{card.label}</p>
                  <p className="text-xs font-bold text-zinc-100">{card.value}</p>
                </div>
              );
            })}
          </div>

          <div className="grid gap-2 lg:grid-cols-5">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-2 lg:col-span-3">
              <p className="text-[10px] font-semibold text-zinc-200">Ingresos del año</p>
              <p className="text-[9px] text-zinc-600">Por mes · ejemplo</p>
              <div className="mt-2 flex h-16 items-end justify-between gap-0.5 px-0.5">
                {chartHeights.map((h, i) => (
                  <div
                    key={i}
                    className="w-full max-w-[10px] rounded-t bg-gradient-to-t from-violet-600 to-violet-400 opacity-90"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-2 lg:col-span-2">
              <p className="text-[10px] font-semibold text-zinc-200">Trimestre fiscal</p>
              <p className="text-[9px] text-zinc-600">IVA e IRPF · T2 · ejemplo</p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between rounded bg-emerald-500/10 px-1.5 py-1 text-[9px]">
                  <span className="text-zinc-400">IVA repercutido</span>
                  <span className="font-semibold text-emerald-400">3.420 €</span>
                </div>
                <div className="flex justify-between rounded bg-amber-500/10 px-1.5 py-1 text-[9px]">
                  <span className="text-zinc-400">IRPF retenido</span>
                  <span className="font-semibold text-amber-400">1.180 €</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2 rounded-lg border border-zinc-800 bg-zinc-900/50 p-2">
            <div className="mb-1.5 flex items-center justify-between">
              <p className="text-[10px] font-semibold text-zinc-200">Últimas facturas</p>
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[8px] font-medium text-violet-300">
                <SparklesIcon className="h-2.5 w-2.5" aria-hidden />
                Importar con IA
              </span>
            </div>
            <div className="space-y-1">
              {mockInvoices.map((inv) => (
                <div key={inv.num} className="flex items-center justify-between rounded-md bg-zinc-800/50 px-2 py-1">
                  <div className="min-w-0">
                    <p className="text-[10px] font-medium text-zinc-100">#{inv.num}</p>
                    <p className="truncate text-[9px] text-zinc-500">{inv.client}</p>
                    <p className="truncate text-[8px] text-zinc-600">{inv.concepts}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-[10px] font-medium text-zinc-200">{inv.amount}</p>
                    <span className={`inline-block rounded px-1 py-0.5 text-[8px] font-medium ${inv.statusClass}`}>
                      {inv.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </BrowserFrame>
  );
}

export const LandingPage: React.FC = () => {
  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen font-sans text-zinc-900 antialiased" style={{ background: 'linear-gradient(135deg, #ede9fe 0%, #f5f3ff 40%, #ede9fe 70%, #ddd6fe 100%)' }}>
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-48 right-[-10%] h-[32rem] w-[32rem] rounded-full bg-violet-400/20 blur-3xl" />
        <div className="absolute top-1/3 left-[-15%] h-[28rem] w-[28rem] rounded-full bg-purple-300/20 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-[20rem] w-[20rem] rounded-full bg-indigo-300/15 blur-3xl" />
      </div>

      <header className="relative z-20 border-b border-violet-200/60 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-purple-700 text-sm font-bold text-white shadow-lg shadow-violet-500/30">
              F
            </div>
            <div>
              <span className="text-lg font-bold tracking-tight text-zinc-900">FactuApp</span>
              <p className="text-xs text-zinc-500">by Entik Media</p>
            </div>
          </div>
          <nav className="hidden items-center gap-8 text-sm font-medium text-zinc-600 md:flex">
            <a href="#funciones" className="transition hover:text-violet-700">
              Funciones
            </a>
            <a href="#panel" className="transition hover:text-violet-700">
              Panel
            </a>
            <a href="#configuracion" className="transition hover:text-violet-700">
              Configuración
            </a>
          </nav>
          <PrimaryCta className="!px-4 !py-2.5" />
        </div>
      </header>

      <main className="relative z-10">
        {/* Hero */}
        <section className="mx-auto max-w-7xl px-4 pb-20 pt-12 sm:px-6 sm:pt-16 lg:px-8 lg:pt-20">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-300/60 bg-white/70 px-4 py-1.5 text-xs font-semibold text-violet-800 shadow-sm">
                <BoltIcon className="h-3.5 w-3.5 text-violet-600" aria-hidden />
                Facturación y control fiscal
              </p>
              <h1 className="text-4xl font-extrabold tracking-tight text-zinc-900 sm:text-5xl lg:text-[3.25rem] lg:leading-[1.08]">
                Tu facturación y visión fiscal,{' '}
                <span className="bg-gradient-to-r from-violet-600 to-purple-700 bg-clip-text text-transparent">
                  en un solo panel
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-zinc-600">
                Emite facturas con IVA e IRPF, gestiona clientes, importa PDF o Excel con IA, automatiza cobros
                recurrentes y consulta ingresos y resumen trimestral — todo lo que incluye tu dashboard.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <PrimaryCta large />
                <a
                  href="#panel"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-700 transition hover:text-violet-700"
                >
                  Ver el panel en acción
                  <ArrowRightIcon className="h-4 w-4" aria-hidden />
                </a>
              </div>
              <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-600">
                <li className="flex items-center gap-1.5">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-500" aria-hidden />
                  5 pestañas principales
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-500" aria-hidden />
                  Envío por email
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-500" aria-hidden />
                  Multiempresa
                </li>
              </ul>
            </div>
            <div className="relative lg:pl-4">
              <div
                className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-violet-500/20 via-purple-400/10 to-transparent blur-2xl"
                aria-hidden
              />
              <DashboardMockPreview />
            </div>
          </div>
        </section>

        {/* Module pills */}
        <section className="border-y border-violet-200/60 bg-white/60 py-8 backdrop-blur-sm">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-6 gap-y-3 px-4 sm:px-6 lg:px-8">
            {appModules.slice(0, 8).map((m) => (
              <span key={m.id} className="text-sm font-medium text-zinc-600">
                {m.name}
                {m.badge && (
                  <span className="ml-1.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                    {m.badge}
                  </span>
                )}
              </span>
            ))}
          </div>
        </section>

        {/* Features */}
        <section id="funciones" className="py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl">Todo lo que incluye FactuApp</h2>
              <p className="mt-4 text-lg text-zinc-600">
                Funciones pensadas para facturar y controlar la fiscalidad sin pasos de más.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((item) => {
                const Icon = item.icon;
                return (
                  <article
                    key={item.title}
                    className={`group relative overflow-hidden rounded-2xl border border-violet-200/60 bg-white/80 p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-violet-400 hover:shadow-lg ${item.span}`}
                  >
                    <div className="mb-4 inline-flex rounded-xl bg-violet-50 p-3 text-violet-700">
                      <Icon className="h-6 w-6" aria-hidden />
                    </div>
                    <h3 className="text-lg font-semibold text-zinc-900">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-600">{item.description}</p>
                  </article>
                );
              })}
              <article className="group relative overflow-hidden rounded-2xl border border-zinc-200/80 bg-gradient-to-br from-zinc-900 to-violet-950 p-6 text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg lg:col-span-2">
                <div className="mb-4 inline-flex rounded-xl bg-white/10 p-3">
                  <ShieldCheckIcon className="h-6 w-6" aria-hidden />
                </div>
                <h3 className="text-lg font-semibold">Copias y Drive</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                  Copias de seguridad en JSON y Google Drive en beta. Integraciones avanzadas disponibles según tu plan y
                  configuración de cuenta.
                </p>
                <div className="mt-6">
                  <PrimaryCta className="!bg-white !text-zinc-900 !shadow-none hover:!bg-zinc-100" />
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* App modules */}
        <section id="modulos" className="border-y border-violet-200/60 bg-white/70 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl">Secciones de la aplicación</h2>
              <p className="mx-auto mt-4 max-w-xl text-lg text-zinc-600">
                El mismo menú que encontrarás al acceder a tu cuenta.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {appModules.map((mod) => {
                const Icon = mod.icon;
                return (
                  <div
                    key={mod.id}
                    className="relative flex flex-col rounded-2xl border border-violet-200/60 bg-white/80 p-6 shadow-sm transition hover:border-violet-400 hover:shadow-md"
                  >
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
                      <Icon className="h-6 w-6" aria-hidden />
                    </div>
                    <h3 className="font-semibold text-zinc-900">
                      {mod.name}
                      {mod.badge && (
                        <span className="ml-2 align-middle rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-700">
                          {mod.badge}
                        </span>
                      )}
                    </h3>
                    <p className="mt-2 flex-1 text-sm text-zinc-600">{mod.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Panel preview */}
        <section id="panel" className="py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl">Tu panel, de un vistazo</h2>
                <p className="mt-4 text-lg text-zinc-600">
                  Métricas de facturación, evolución de ingresos y resumen trimestral de IVA e IRPF, calculados con tus
                  facturas emitidas y cobradas.
                </p>
                <ul className="mt-8 space-y-4">
                  {statCards.map((kpi) => {
                    const Icon = kpi.icon;
                    return (
                      <li key={kpi.label} className="flex items-center gap-4">
                        <div className={`rounded-lg p-2 ${kpi.bg}`}>
                          <Icon className={`h-5 w-5 ${kpi.color}`} aria-hidden />
                        </div>
                        <div>
                          <p className="font-semibold text-zinc-800">{kpi.label}</p>
                          <p className="text-sm text-zinc-500">
                            Vista de ejemplo · en la app, con tus datos reales
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-6 flex items-center gap-2 text-sm text-zinc-500">
                  <CommandLineIcon className="h-5 w-5 shrink-0 text-zinc-400" aria-hidden />
                  Atajo ⌘K para ir a facturas, importar, gastos o crear una factura nueva.
                </p>
              </div>
              <DashboardMockPreview />
            </div>
          </div>
        </section>

        {/* Settings */}
        <section id="configuracion" className="border-y border-violet-200/60 bg-white/60 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 max-w-2xl">
              <h2 className="text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl">Configuración centralizada</h2>
              <p className="mt-4 text-lg text-zinc-600">
                Correo, inteligencia artificial, copias de seguridad, Google Drive y gestión multiempresa, desde un
                único lugar.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {settingsItems.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.name}
                    className="rounded-2xl border border-violet-200/60 bg-white/80 p-6 shadow-sm transition hover:border-violet-400 hover:shadow-md"
                  >
                    <Icon className="mb-4 h-8 w-8 text-violet-600" aria-hidden />
                    <h3 className="font-semibold text-zinc-900">{item.name}</h3>
                    <p className="mt-1 text-sm text-zinc-500">{item.description}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm text-zinc-500">
              <Cog6ToothIcon className="h-5 w-5 shrink-0 text-zinc-400" aria-hidden />
              También accesible desde la pestaña Ajustes del menú lateral.
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-purple-700 to-violet-800 px-8 py-16 text-center text-white shadow-2xl sm:px-16">
              <div className="pointer-events-none absolute inset-0 opacity-30" aria-hidden>
                <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
                <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-purple-400/30 blur-3xl" />
              </div>
              <div className="relative">
                <h2 className="text-3xl font-bold sm:text-4xl">Empieza cuando quieras</h2>
                <p className="mx-auto mt-4 max-w-lg text-lg text-violet-100">
                  Accede con tu cuenta y retoma facturas, importaciones, gastos y envíos donde los dejaste.
                </p>
                <div className="mt-10">
                  <Link
                    to={LOGIN_PATH}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-4 text-base font-semibold text-violet-700 shadow-lg transition hover:bg-violet-50"
                  >
                    Acceder a FactuApp
                    <ArrowRightIcon className="h-5 w-5" aria-hidden />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-violet-200/60 bg-white/70 py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 text-center text-sm text-zinc-500 sm:flex-row sm:px-6 sm:text-left lg:px-8">
          <p className="font-medium text-zinc-700">FactuApp · Entik Media</p>
          <p>© {year} Entik Media</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
